// @ts-nocheck
import React, { useMemo, useState, useEffect } from 'react';
import KwPanel from '../common/KwPanel';
import { useDropzone } from 'react-dropzone';
import Loading from '../common/Loading';
import './UploadPricing.css';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';

import image_xlsx_file_with_content from '../../content/images/csv-file-with-content.svg';
import image_upload_arrow from '../../content/images/upload-arrow.svg';
import ViewLink from './components/ViewLink';
import SuccessIcon from './components/SuccessIcon';
import { usePageTitle } from '../../util/pageTitle';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { clearGameSkuPricingUpload } from '../../reducers/gameSKUPricingUploadReducer';
import { uploadPrices } from '../../api/skuPriceApi';

function UploadPricing({ setClasses }) {
  usePageTitle(['Upload pricing']);

  const dispatch = useAppDispatch();
  const gameSKUPricingUploadResults = useAppSelector(
    (state) => state.gameSKUPricingUploadResults
  );

  const parentRef = React.useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: gameSKUPricingUploadResults.updates.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    overscan: 20,
  });

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [processingStarted, setProcessingStarted] = useState(false);
  useEffect(() => {
    setClasses('h-100');
  }, [setClasses]);

  useEffect(() => {
    if (gameSKUPricingUploadResults?.failure) {
      toast.error(
        `${t('Price upload failed')}: ${gameSKUPricingUploadResults.message}`
      );
      setProcessingStarted(false);
    }
  }, [gameSKUPricingUploadResults, t]);

  const onDrop = (droppedFiles) => {
    dispatch(clearGameSkuPricingUpload());
    setLoading(true);
    uploadPrices({ file: droppedFiles[0] })
      .then(() => {
        setLoading(false);
        toast.success(t('Price upload started'));
        setProcessingStarted(true);
      })
      .catch(() => {
        setLoading(false);
        toast.error(t('Failed to upload prices'));
      });
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ onDrop });
  const classeNames = useMemo(
    () =>
      `dropzone game-upload-action-panel ${isDragActive ? 'active' : ''} ${
        isDragAccept ? 'accept positive-colour1' : ''
      } ${isDragReject ? 'reject' : ''}`,
    [isDragActive, isDragReject, isDragAccept]
  );

  return (
    <>
      {!loading ? (
        <KwPanel className="mb-4">
          <div className="game-upload-wrapper">
            <div className="game-upload-action-panel game-upload-divider ps-lg-5 pe-lg-5">
              <img
                src={image_upload_arrow}
                alt={t('Arrow')}
                width="100"
                height="100"
                className="space-bottom"
              />
            </div>
            <div {...getRootProps({ className: classeNames })}>
              <input {...getInputProps()} />
              <img
                src={image_xlsx_file_with_content}
                alt={t('Upload your pricing')}
                className="space-bottom"
              />
              <p>{t('Drag & drop Steam price export here')}</p>
            </div>
          </div>
        </KwPanel>
      ) : (
        <Loading />
      )}

      {processingStarted && (
        <>
          <div className="progress">
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              aria-valuenow={gameSKUPricingUploadResults?.percentage ?? 0}
              aria-valuemin="0"
              aria-valuemax="100"
              style={{
                width: `${gameSKUPricingUploadResults?.percentage ?? 0}%`,
              }}
            />
          </div>
          <KwPanel className="flex-1 mb-4">
            <div
              ref={parentRef}
              className="overflow-auto"
              style={{ height: 'calc(100vh - 400px)' }}
            >
              <div style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                <table className="table dataTable width-100">
                  <thead>
                    <tr>
                      <td style={{ width: '5%' }}></td>
                      <td style={{ width: '5%' }}>{t('Row')}</td>
                      <td style={{ width: '25%' }}>{t('SKU')}</td>
                      <td style={{ width: '25%' }}>{t('Package Id')}</td>
                      <td style={{ width: '35%' }}>{t('Message')}</td>
                      <td style={{ width: '5%' }}></td>
                    </tr>
                  </thead>
                  <tbody>
                    {rowVirtualizer
                      .getVirtualItems()
                      .map((virtualRow, index) => {
                        const result =
                          gameSKUPricingUploadResults?.updates[
                            virtualRow.index
                          ];

                        return (
                          <tr
                            key={result.row === -1 ? result.skuId : result.row}
                            style={{
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${
                                virtualRow.start - index * virtualRow.size
                              }px)`,
                            }}
                          >
                            <td>
                              <SuccessIcon result={result}></SuccessIcon>
                            </td>
                            <td>{result.row < 0 ? '' : result.row}</td>
                            <td>{result.packageName}</td>
                            <td>{result.packageId}</td>
                            <td>{result.message}</td>
                            <td>
                              <ViewLink result={result}></ViewLink>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </KwPanel>
        </>
      )}
    </>
  );
}

export default UploadPricing;
